/**
 * What a selected neuron responds to at the pixel level.
 *
 * The first hidden layer's kernel has one row per pixel, so its neurons'
 * receptive fields are simply one kernel column. Deeper neurons have no direct
 * pixel weights, so the selection is propagated backwards through the kernels
 * (`v ← kernel · v` per layer). That is the chain rule with every activation
 * derivative set to 1: exact for the first hidden layer, and a linearized
 * sensitivity estimate for anything above it.
 *
 * Pure — plain numbers in, plain numbers out — so the mapping is unit-testable
 * and the graph stays the only module that touches SVG.
 */
import { MNIST } from '../../shared/mnist/idx.ts';
import type { LayerSnapshot, NetworkSnapshot } from '../engine/types.ts';

/** Pixel identifiers from `inputNeuronId`; the kernel row order is never assumed. */
const PIXEL_ID = /^in:(\d+)$/;

/** One backward step: kernel is `[input][output]`, so it maps outputs onto inputs. */
function propagateBackward(layer: LayerSnapshot, vector: Float32Array): Float32Array {
  const next = new Float32Array(layer.inputIds.length);
  for (let input = 0; input < next.length; input += 1) {
    const row = layer.kernel[input];
    if (!row) {
      continue;
    }
    let sum = 0;
    for (let unit = 0; unit < vector.length; unit += 1) {
      sum += (row[unit] ?? 0) * (vector[unit] ?? 0);
    }
    next[input] = sum;
  }
  return next;
}

/**
 * Signed sensitivity of one neuron to each of the 784 input pixels.
 *
 * Values are normalized to this neuron's own strongest pixel, so the overlay
 * always spans its full ramp and two neurons can be compared by shape rather
 * than by absolute magnitude — which differs by orders of magnitude between
 * layers as the propagation multiplies weights together.
 *
 * Returns `null` for an id the snapshot does not contain, so a stale selection
 * cannot paint a meaningless map.
 */
export function pixelSensitivity(snapshot: NetworkSnapshot, neuronId: string): Float32Array | null {
  const targetIndex = snapshot.layers.findIndex((layer) => layer.neuronIds.includes(neuronId));
  const target = snapshot.layers[targetIndex];
  if (!target) {
    return null;
  }

  const vector = new Float32Array(target.neuronIds.length);
  const unit = target.neuronIds.indexOf(neuronId);
  vector[unit] = 1;

  let sensitivities: Float32Array = vector;
  for (let index = targetIndex; index >= 0; index -= 1) {
    const layer = snapshot.layers[index];
    if (!layer) {
      return null;
    }
    sensitivities = propagateBackward(layer, sensitivities);
  }

  // `inputIds` is authoritative for row order; a row that is not a pixel id is
  // skipped rather than guessed at.
  const pixels = new Float32Array(MNIST.pixels);
  const rows = snapshot.layers[0]?.inputIds ?? [];
  rows.forEach((id, row) => {
    const match = PIXEL_ID.exec(id);
    const index = match?.[1] === undefined ? Number.NaN : Number(match[1]);
    if (Number.isInteger(index) && index >= 0 && index < pixels.length) {
      pixels[index] = sensitivities[row] ?? 0;
    }
  });

  let scale = 0;
  for (const value of pixels) {
    scale = Math.max(scale, Math.abs(value));
  }
  // An all-zero field has nothing to show, so the tile stays a plain digit.
  if (scale === 0) {
    return null;
  }
  for (let i = 0; i < pixels.length; i += 1) {
    pixels[i] = (pixels[i] ?? 0) / scale;
  }

  return pixels;
}
